"use client";

import { useActionState } from "react";
import {
  requestEmailCode,
  verifyEmailCodeAndLogin,
  type RequestCodeState,
  type VerifyCodeState,
} from "@/lib/auth-actions";

const inputClass =
  "w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-base outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 dark:border-white/15 dark:bg-zinc-900";

const buttonClass =
  "mt-2 w-full rounded-xl bg-indigo-600 px-4 py-3 text-base font-semibold text-white transition active:scale-[0.99] disabled:opacity-50";

export function LoginForm() {
  const [reqState, reqAction, reqPending] = useActionState<
    RequestCodeState,
    FormData
  >(requestEmailCode, undefined);

  const sent = reqState && "ok" in reqState ? reqState : null;

  // 2단계: 코드 입력 (이메일 발송 성공 후)
  if (sent) {
    return <CodeStep email={sent.email} />;
  }

  // 1단계: 이메일 입력
  return (
    <form action={reqAction} className="flex flex-col gap-4">
      <div>
        <label htmlFor="email" className="mb-1.5 block text-sm font-medium">
          이메일
        </label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          placeholder="you@example.com"
          className={inputClass}
        />
      </div>

      {reqState && "error" in reqState ? (
        <p role="alert" className="text-sm text-red-600">
          {reqState.error}
        </p>
      ) : null}

      <button type="submit" disabled={reqPending} className={buttonClass}>
        {reqPending ? "보내는 중…" : "인증코드 받기"}
      </button>
    </form>
  );
}

function CodeStep({ email }: { email: string }) {
  const [state, action, pending] = useActionState<VerifyCodeState, FormData>(
    verifyEmailCodeAndLogin,
    undefined,
  );

  return (
    <form action={action} className="flex flex-col gap-4">
      <input type="hidden" name="email" value={email} />

      <div
        role="status"
        className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-center text-sm text-indigo-900 dark:border-indigo-900/40 dark:bg-indigo-950/30 dark:text-indigo-200"
      >
        <b className="font-semibold">{email}</b> 으로
        <br />
        6자리 인증코드를 보냈어요. (5분간 유효)
      </div>

      <div>
        <label htmlFor="code" className="mb-1.5 block text-sm font-medium">
          인증코드
        </label>
        <input
          id="code"
          name="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d{6}"
          maxLength={6}
          required
          autoFocus
          placeholder="000000"
          className={`${inputClass} text-center text-2xl tracking-[0.4em]`}
        />
      </div>

      {state?.error ? (
        <p role="alert" className="text-sm text-red-600">
          {state.error}
        </p>
      ) : null}

      <button type="submit" disabled={pending} className={buttonClass}>
        {pending ? "확인 중…" : "로그인"}
      </button>

      <a
        href="/login"
        className="text-center text-sm text-zinc-500 hover:underline dark:text-zinc-400"
      >
        이메일 다시 입력
      </a>
    </form>
  );
}
